// One seat-fit definition for Places collections. Policy owns the Tight edge.
import { T } from "../app/tokens.js";
export const DEFAULT_BUFFER = 10;
export const FIT = Object.freeze({
  UNDER: "under", RIGHT: "comfortable", COMFORTABLE: "comfortable",
  TIGHT: "tight", OVER: "over", DENSIFY: "over",
  NO_DATA: "no_data", PARTIAL: "partial", NO_SEATS: "no_seats",
  EXCLUDED: "excluded", HISTORY: "history",
});
export const FIT_LABEL = Object.freeze({
  under: "Under-Used", comfortable: "Right-Sized", tight: "Tight", over: "Over-Cap",
  no_data: "No data", partial: "Partial", no_seats: "No seats",
  excluded: "No active office", history: "Under 3 months history",
});
export const FIT_STYLE = {
  under: { bg: T.ragAmberBg, ink: T.ragAmberText, border: T.ragAmberBorder },
  comfortable: { bg: "var(--v2-green-tint)", ink: "var(--v2-green)", border: "var(--v2-green-border)" },
  tight: { bg: T.ragAmberBg, ink: T.ragAmberText, border: T.ragAmberBorder },
  over: { bg: "var(--v2-red-tint)", ink: "var(--v2-red)", border: "var(--v2-red-border)" },
};
export const isFitBand = status => Object.hasOwn(FIT_STYLE, status);
export const tightBoundary = buffer => (100 - (Number.isFinite(buffer) ? buffer : DEFAULT_BUFFER)) / 100;
export function classifySeatFit(demand, usable, buffer = DEFAULT_BUFFER) {
  if (demand == null || !Number.isFinite(demand) || demand < 0) return FIT.PARTIAL;
  if (usable == null || !Number.isFinite(usable) || usable < 0) return FIT.NO_DATA;
  if (usable === 0) return demand > 0 ? FIT.OVER : FIT.NO_SEATS;
  const ratio = demand / usable;
  if (ratio < .8) return FIT.UNDER;
  if (ratio < tightBoundary(buffer)) return FIT.RIGHT;
  return ratio <= 1 ? FIT.TIGHT : FIT.OVER;
}
export function seatFitBands(buffer = DEFAULT_BUFFER) {
  const edge = tightBoundary(buffer) * 100;
  return [
    { status: FIT.UNDER, description: "less than 80% of usable seats" },
    { status: FIT.RIGHT, description: `80% to below ${edge}% of usable seats` },
    { status: FIT.TIGHT, description: `${edge}% through 100% of usable seats, inclusive` },
    { status: FIT.OVER, description: "above 100% of usable seats" },
  ].map(b => ({ ...b, label: FIT_LABEL[b.status], style: FIT_STYLE[b.status] }));
}
export function densifyingDisclosure(demand, usable, maximum) {
  return Number.isFinite(demand) && Number.isFinite(usable) && demand > usable
    && Number.isFinite(maximum) && demand <= maximum
    ? `Over usable seats; fits within max ${Math.round(maximum)} by densifying` : "";
}
export function officeSeatFit(office) {
  const source = office.seatFitToday;
  const status = !["operational", "dormant"].includes(office.stage) ? FIT.EXCLUDED
    : source?.state === "loading" ? "loading" : source?.state === "error" ? "error"
      : classifySeatFit(office.latestHeadcount?.totalCount ?? null, source?.usableSeats, source?.bufferPct);
  const dense = Number.isFinite(office.analyticalSqft) && office.installedCapacity > 0
    && office.analyticalSqft / office.installedCapacity < 150;
  return { status, verdict: isFitBand(status) ? FIT_LABEL[status] : null, dense,
    disclosure: `Assigned today ${office.latestHeadcount?.totalCount ?? "unknown"} vs usable seats ${source?.usableSeats ?? "unknown"} (${office.installedCapacity ?? "unknown"} installed seats). ${densifyingDisclosure(office.latestHeadcount?.totalCount, source?.usableSeats, source?.maximumSeats)} ${source?.error ?? ""}` };
}
